import React from 'react'
import InputMask from 'react-input-mask'
import TextField, { BaseTextFieldProps } from '@mui/material/TextField'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { IconButton, Box, Typography } from '@mui/material'
import {
  inputContainerStyle,
  inputLabelStyle,
  iconButtonStyle,
  errorLabelStyle
} from './Input.styles'

interface InputPropsI extends BaseTextFieldProps {
  placeholder?: string
  filled?: string
  icon?: {
    direction: 'start' | 'end'
    icon: React.ReactNode
  }
  error?: boolean
  toggleVisible?: () => void
  securityText?: boolean
  errorMessage?: string
  value?: string
  mask?: string
  enabled?: boolean
  type?: 'text' | 'password' | 'number'
  labelText?: string
  labelTextAnimated?: boolean
  variant?: 'outlined'
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  sizeContainer?: 'auto' | 'small'
  centerValue?: boolean
  maxLength?: number
}

const Input = React.memo(
  React.forwardRef<HTMLInputElement, InputPropsI>(
    (
      {
        enabled,
        variant = 'outlined',
        labelText,
        labelTextAnimated,
        icon,
        mask,
        placeholder,
        onChange,
        toggleVisible,
        error,
        errorMessage,
        securityText = false,
        sizeContainer = 'auto',
        centerValue = false,
        maxLength,
        type = 'text',
        ...rest
      },
      ref
    ) => {
      const iconProps = React.useMemo(() => {
        if (icon) {
          return {
            [icon.direction === 'end' ? 'endAdornment' : 'startAdornment']:
              icon.icon
          }
        }
        if (toggleVisible) {
          return {
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={toggleVisible}
                edge="end"
              >
                {securityText ? (
                  <Visibility sx={iconButtonStyle} />
                ) : (
                  <VisibilityOff sx={iconButtonStyle} />
                )}
              </IconButton>
            )
          }
        }
        return undefined
      }, [icon, toggleVisible, securityText])

      return (
        <Box sx={inputContainerStyle(sizeContainer)}>
          {labelText && !labelTextAnimated && (
            <Typography variant="subtitle1" sx={inputLabelStyle(enabled)}>
              {labelText}
            </Typography>
          )}
          {mask ? (
            <InputMask
              mask={mask}
              value={rest.value}
              onChange={onChange}
              maskPlaceholder=""
            >
              <TextField
                variant={variant}
                fullWidth
                placeholder={placeholder || mask}
                InputProps={iconProps}
                label={labelTextAnimated ? labelText : ''}
                type={type}
                inputProps={{
                  style: { textAlign: centerValue ? 'center' : 'left' },
                  maxLength
                }}
                {...rest}
                ref={ref} // Use ref diretamente aqui
              />
            </InputMask>
          ) : (
            <TextField
              variant={variant}
              fullWidth
              placeholder={placeholder}
              InputProps={iconProps}
              inputProps={{
                style: { textAlign: centerValue ? 'center' : 'left' },
                maxLength
              }}
              label={labelTextAnimated ? labelText : ''}
              type={securityText ? 'password' : 'text'}
              onChange={onChange}
              {...rest}
              inputRef={ref}
            />
          )}
          {error && errorMessage && (
            <Typography variant="body2" sx={errorLabelStyle}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      )
    }
  )
)

export default Input
